/**
 * App Roles, as read from the id_token's `roles` claim.
 *
 * ╔═══════════════════════════════════════════════════════════════════════════════════════╗
 * ║ 🔴 THE SPA IS NOT A SECURITY BOUNDARY. IT NEVER WILL BE.                              ║
 * ║                                                                                       ║
 * ║ Everything in this file exists to HIDE UI THAT WOULD ONLY FAIL — nothing more. The    ║
 * ║ authorisation decision is made in the API, which validates the access token against   ║
 * ║ the API app registration's `aud` and checks the roles claim server-side               ║
 * ║ (architecture-proposal §3.2, "Identity").                                             ║
 * ║                                                                                       ║
 * ║ The roles below come from a token in the user's own browser. A user can edit them.    ║
 * ║ If hiding a button is the only thing stopping an action, the action is unprotected.   ║
 * ║ Every guarded call must fail closed at the API with 403, and the SPA must render that ║
 * ║ 403 correctly rather than assume it cannot happen.                                    ║
 * ╚═══════════════════════════════════════════════════════════════════════════════════════╝
 */

/** Matches the App Roles defined on the API app registration (architecture-proposal §3.1). */
export const appRoles = [
  'Kassenwart',
  'Vorstand',
  'Kellermannenleiter',
  'Pruefer',
  'Leser',
] as const

export type AppRole = (typeof appRoles)[number]

export function isAppRole(value: unknown): value is AppRole {
  return typeof value === 'string' && (appRoles as readonly string[]).includes(value)
}

/** Extracts the roles claim from an MSAL account's id_token claims. Unknown roles are dropped. */
export function rolesFromClaims(claims: Record<string, unknown> | undefined): readonly AppRole[] {
  const raw = claims?.['roles']
  return Array.isArray(raw) ? raw.filter(isAppRole) : []
}
